import React, { useState, useCallback } from "react";
import {
  Modal,
  Upload,
  Table,
  Button,
  message,
  Typography,
  Steps,
  Card,
  Select,
  Row,
  Col,
  Alert,
  Space,
  Progress,
  Divider,
  Tag,
  Input,
} from "antd";
import {
  UploadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import type { ColumnsType } from "antd/es/table";
import * as XLSX from "xlsx";
import { supabase } from "../../../lib/supabase";

const { Title, Text } = Typography;
const { Step } = Steps;
const { Option } = Select;

// Define the expected database columns for booking_product
const DATABASE_COLUMNS = [
  { key: "name", label: "Product Name", required: true, type: "text" },
  { key: "hs_code", label: "HS Code", required: false, type: "text" },
  {
    key: "gross_weight",
    label: "Gross Weight (kg)",
    required: false,
    type: "number",
  },
  { key: "cbm", label: "CBM (m³)", required: false, type: "number" },
  { key: "value", label: "Value (USD)", required: false, type: "number" },
  { key: "ean", label: "EAN/Barcode", required: false, type: "bigint" },
  { key: "pieces", label: "Pieces", required: false, type: "number" },
  { key: "cartons", label: "Cartons", required: false, type: "number" },
];

interface BookingProductImportProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  bookingId: number;
  companyId: number;
}

interface ParsedData {
  headers: string[];
  rows: unknown[][];
  preview: Record<string, unknown>[];
}

interface ColumnMapping {
  [csvColumn: string]: string; // Maps CSV column to database column
}

interface ValidationError {
  row: number;
  column: string;
  value: unknown;
  error: string;
}

export const BookingProductImport: React.FC<BookingProductImportProps> = ({
  visible,
  onCancel,
  onSuccess,
  bookingId,
  companyId,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  );
  const [importPreview, setImportPreview] = useState<Record<string, unknown>[]>(
    []
  );
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [editedValues, setEditedValues] = useState<Map<string, string>>(
    new Map()
  );

  // Helper function to find the actual product table in the data
  const findProductTable = (
    jsonData: unknown[][]
  ): { tableStartIndex: number; headers: string[]; rows: unknown[][] } => {
    // Common product table header indicators
    const productHeaderIndicators = [
      "product",
      "name",
      "hs code",
      "h.s. code",
      "quantity",
      "pcs",
      "pieces",
      "carton",
      "ctns",
      "cbm",
      "weight",
      "price",
      "amount",
      "value",
      "ean",
      "no",
      "item",
      "description",
      "unit",
      "gross",
      "net",
      "total",
      // Dutch variations
      "naam",
      "aantal",
      "gewicht",
      "prijs",
      "waarde",
      "stuks",
      // Chinese/other variations
      "code",
      "material",
      "size",
      "g.m",
      "kgs",
    ];

    for (let i = 0; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (!Array.isArray(row) || row.length < 2) continue;

      // Convert row to strings and check for header indicators
      const rowStrings = row
        .map((cell) => (cell ? String(cell).toLowerCase().trim() : ""))
        .filter((str) => str.length > 0);

      // Count how many cells in this row match product header indicators
      const matchCount = rowStrings.reduce((count, cellValue) => {
        const hasMatch = productHeaderIndicators.some(
          (indicator) =>
            cellValue.includes(indicator) || indicator.includes(cellValue)
        );
        return hasMatch ? count + 1 : count;
      }, 0);

      // If this row has at least 3 matches and at least 4 non-empty cells, consider it a header row
      if (matchCount >= 3 && rowStrings.length >= 4) {
        const headers = row
          .map((cell) => (cell ? String(cell).trim() : ""))
          .filter((h) => h.length > 0);
        const dataRows = jsonData.slice(i + 1);

        // Validate that we have actual data rows after this header
        const validDataRows = dataRows.filter((dataRow) => {
          if (!Array.isArray(dataRow)) return false;
          // Check if this row has data in the same positions as headers
          let nonEmptyCount = 0;
          for (let j = 0; j < Math.min(headers.length, dataRow.length); j++) {
            if (
              dataRow[j] !== null &&
              dataRow[j] !== undefined &&
              String(dataRow[j]).trim() !== ""
            ) {
              nonEmptyCount++;
            }
          }
          return nonEmptyCount >= 2; // At least 2 fields should have data
        });

        if (validDataRows.length > 0) {
          return {
            tableStartIndex: i,
            headers,
            rows: validDataRows,
          };
        }
      }
    }

    // Fallback: if no clear table found, assume first row is headers (original behavior)
    if (jsonData.length >= 2) {
      const firstRow = jsonData[0];
      if (Array.isArray(firstRow)) {
        const headers = firstRow
          .map((cell) => (cell ? String(cell).trim() : ""))
          .filter((h) => h.length > 0);
        if (headers.length >= 2) {
          return {
            tableStartIndex: 0,
            headers,
            rows: jsonData.slice(1),
          };
        }
      }
    }

    return {
      tableStartIndex: -1,
      headers: [],
      rows: [],
    };
  };

  // Step 1: File Upload
  const handleFileUpload = useCallback((file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        let workbook: XLSX.WorkBook;

        if (file.name.endsWith(".csv")) {
          // Parse CSV
          const csvData = data as string;
          workbook = XLSX.read(csvData, { type: "string" });
        } else {
          // Parse Excel
          workbook = XLSX.read(data, { type: "array" });
        }

        // Get first worksheet
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];

        // Convert to JSON with header option
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          message.error(
            "File must contain at least a header row and one data row"
          );
          return;
        }

        // Find the actual product table by looking for common product-related headers
        const { tableStartIndex, headers, rows } = findProductTable(
          jsonData as unknown[][]
        );

        if (tableStartIndex === -1 || headers.length === 0) {
          message.error(
            'Could not find product table in the file. Please ensure the file contains product data with headers like "Product Name", "H.S. Code", "Quantity", etc.'
          );
          return;
        }

        // Filter out empty rows
        const filteredRows = rows.filter(
          (row) =>
            Array.isArray(row) &&
            row.some(
              (cell) => cell !== null && cell !== undefined && cell !== ""
            )
        );

        if (filteredRows.length === 0) {
          message.error("No product data found in the file");
          return;
        }

        // Create preview data (all rows)
        const preview = filteredRows.map((row, index) => {
          const previewRow: Record<string, unknown> = { key: index };
          headers.forEach((header, headerIndex) => {
            previewRow[header] = Array.isArray(row)
              ? row[headerIndex] || ""
              : "";
          });
          return previewRow;
        });

        setParsedData({
          headers,
          rows: filteredRows,
          preview,
        });

        // Auto-exclude common total/summary rows
        const autoExcludedRows = new Set<number>();
        filteredRows.forEach((row, index) => {
          if (Array.isArray(row)) {
            const rowString = row.join(" ").toLowerCase();
            // Common patterns for total/summary rows
            const totalPatterns = [
              "total",
              "sum",
              "subtotal",
              "grand total",
              "amount",
              "shipping mark",
              "remark",
              "note",
              "memo",
              "total amount",
              "final",
              "summary",
            ];

            if (totalPatterns.some((pattern) => rowString.includes(pattern))) {
              autoExcludedRows.add(index);
            }
          }
        });

        setExcludedRows(autoExcludedRows);

        if (autoExcludedRows.size > 0) {
          message.info(
            `Auto-excluded ${autoExcludedRows.size} total/summary row(s). You can adjust this in the preview.`
          );
        }

        // Auto-map columns based on common names
        const autoMapping: ColumnMapping = {};
        headers.forEach((header) => {
          const lowerHeader = header.toLowerCase().trim();
          const dbColumn = DATABASE_COLUMNS.find((col) => {
            const variations = [
              col.key,
              col.label.toLowerCase(),
              col.label.toLowerCase().replace(/[^\w]/g, ""),
              ...getColumnVariations(col.key),
            ];
            return variations.some(
              (variation) =>
                lowerHeader.includes(variation) ||
                variation.includes(lowerHeader)
            );
          });

          if (dbColumn) {
            autoMapping[header] = dbColumn.key;
          }
        });

        setColumnMapping(autoMapping);
        setCurrentStep(1);

        if (tableStartIndex > 0) {
          message.success(
            `File parsed successfully. Found product table starting at row ${
              tableStartIndex + 1
            }. ${filteredRows.length} product rows detected.`
          );
        } else {
          message.success(
            `File parsed successfully. Found ${filteredRows.length} product rows.`
          );
        }
      } catch (error) {
        console.error("Error parsing file:", error);
        message.error("Error parsing file. Please check the file format.");
      }
    };

    if (file.name.endsWith(".csv")) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }

    return false; // Prevent default upload
  }, []);

  // Helper function to get column variations for auto-mapping
  const getColumnVariations = (key: string): string[] => {
    const variations: { [key: string]: string[] } = {
      name: [
        "product name",
        "productname",
        "naam",
        "product",
        "title",
        "description",
      ],
      hs_code: [
        "hs code",
        "hscode",
        "hs_code",
        "harmonized code",
        "tariff code",
        "h.s. code",
        "h.s code",
        "hs",
      ],
      gross_weight: [
        "gross weight",
        "weight",
        "gewicht",
        "gross_weight",
        "bruto gewicht",
        "g.m",
        "kgs",
        "g.m (kgs)",
        "gross",
        "total weight",
      ],
      cbm: [
        "cbm",
        "volume",
        "cubic meters",
        "kubikmeter",
        "m3",
        "cbm(m³)",
        "cbm(m3)",
        "cbm (m³)",
        "cbm (m3)",
        "cubic",
      ],
      value: [
        "value",
        "price",
        "waarde",
        "prijs",
        "amount",
        "unit price",
        "unit price(usd)",
        "price(usd)",
        "amount (usd)",
        "total",
      ],
      ean: [
        "ean",
        "barcode",
        "gtin",
        "upc",
        "product code",
        "ean codes",
        "item code",
        "code",
      ],
      pieces: [
        "pieces",
        "stuks",
        "quantity",
        "qty",
        "aantal",
        "pcs",
        "quantity (pcs)",
        "quantity(pcs)",
        "pcs/ctn",
        "pc",
      ],
      cartons: [
        "cartons",
        "boxes",
        "dozen",
        "kartons",
        "ctns",
        "carton",
        "ctn",
        "carton size(cm)",
        "carton size",
        "box",
      ],
    };

    return variations[key] || [];
  };

  // Step 2: Column Mapping
  const handleColumnMappingChange = (csvColumn: string, dbColumn: string) => {
    setColumnMapping((prev) => {
      const newMapping = { ...prev };

      if (dbColumn) {
        // Add or update the mapping
        newMapping[csvColumn] = dbColumn;
      } else {
        // Remove the mapping if dbColumn is undefined/empty (user cleared it)
        delete newMapping[csvColumn];
      }

      return newMapping;
    });
    // Reset validation state when mapping changes
    setValidationErrors([]);
    setShowImportPreview(false);
    setImportPreview([]);
  };

  // Step 3: Validation
  const validateData = useCallback(() => {
    if (!parsedData) return;

    const errors: ValidationError[] = [];
    const requiredColumns = DATABASE_COLUMNS.filter((col) => col.required);

    // Check if required columns are mapped
    requiredColumns.forEach((col) => {
      const isMapped = Object.values(columnMapping).includes(col.key);
      if (!isMapped) {
        errors.push({
          row: -1,
          column: col.key,
          value: "",
          error: `Required column "${col.label}" is not mapped`,
        });
      }
    });

    // Validate data in each row (excluding excluded rows)
    parsedData.rows.forEach((row, rowIndex) => {
      // Skip excluded rows
      if (excludedRows.has(rowIndex)) return;
      Object.entries(columnMapping).forEach(([csvColumn, dbColumn]) => {
        // Skip if dbColumn is undefined or empty
        if (!dbColumn) return;

        const csvColumnIndex = parsedData.headers.indexOf(csvColumn);
        const value = Array.isArray(row) ? row[csvColumnIndex] : undefined;
        const dbColumnDef = DATABASE_COLUMNS.find(
          (col) => col.key === dbColumn
        );

        if (!dbColumnDef) return;

        // Check required fields
        if (
          dbColumnDef.required &&
          (value === null || value === undefined || value === "")
        ) {
          errors.push({
            row: rowIndex + 1,
            column: dbColumn,
            value,
            error: `Required field is empty`,
          });
        }

        // Check data types
        if (value !== null && value !== undefined && value !== "") {
          if (dbColumnDef.type === "number" && isNaN(Number(value))) {
            errors.push({
              row: rowIndex + 1,
              column: dbColumn,
              value,
              error: `Invalid number format`,
            });
          } else if (
            dbColumnDef.type === "integer" &&
            (!Number.isInteger(Number(value)) || isNaN(Number(value)))
          ) {
            errors.push({
              row: rowIndex + 1,
              column: dbColumn,
              value,
              error: `Invalid integer format`,
            });
          } else if (
            dbColumnDef.type === "bigint" &&
            (isNaN(Number(value)) || Number(value) < 0)
          ) {
            errors.push({
              row: rowIndex + 1,
              column: dbColumn,
              value,
              error: `Invalid positive integer format`,
            });
          }
        }
      });
    });

    setValidationErrors(errors);

    if (errors.length === 0) {
      // Generate import preview when validation passes (excluding excluded rows)
      const preview = parsedData.rows
        .map((row, index) => ({ row, index }))
        .filter(({ index }) => !excludedRows.has(index))
        .map(({ row, index }) => {
          const previewRow: Record<string, unknown> = {
            key: index,
            row_number: index + 1,
          };

          // Add mapped columns
          Object.entries(columnMapping).forEach(([csvColumn, dbColumn]) => {
            const csvColumnIndex = parsedData.headers.indexOf(csvColumn);
            let value = Array.isArray(row) ? row[csvColumnIndex] : undefined;

            // Convert values based on column type (same logic as import)
            const dbColumnDef = DATABASE_COLUMNS.find(
              (col) => col.key === dbColumn
            );
            if (
              dbColumnDef &&
              value !== null &&
              value !== undefined &&
              value !== ""
            ) {
              if (
                dbColumnDef.type === "number" ||
                dbColumnDef.type === "integer" ||
                dbColumnDef.type === "bigint"
              ) {
                value = Number(value);
              }
            } else if (value === "" || value === null || value === undefined) {
              value = null;
            }

            previewRow[dbColumn] = value;
          });

          return previewRow;
        });

      setImportPreview(preview);
      setShowImportPreview(true);
      setCurrentStep(3);
      message.success("Data validation passed successfully!");
    } else {
      setShowImportPreview(false);
      message.error(
        `Found ${errors.length} validation errors. Please review and fix them.`
      );
    }
  }, [parsedData, columnMapping]);

  // Step 4: Import
  const handleImport = async () => {
    if (!parsedData) return;

    // Verify we have valid company and booking IDs
    if (!companyId) {
      message.error("No company ID provided. Cannot import products.");
      return;
    }

    if (!bookingId) {
      message.error("No booking ID provided. Cannot import products.");
      return;
    }

    setImporting(true);
    setImportProgress(0);

    try {
      console.log(
        `Importing ${parsedData.rows.length} products for booking ${bookingId} and company ${companyId}`
      );

      // Verify that the booking exists and belongs to the specified company
      const { data: bookingVerification, error: verificationError } =
        await supabase
          .from("bookings")
          .select(
            `
            id,
            reference,
            company_id,
            status,
            created_at,
            company:company(name)
          `
          )
          .eq("id", bookingId)
          .single();

      if (verificationError) {
        throw new Error(
          `Failed to verify booking: ${verificationError.message}`
        );
      }

      if (!bookingVerification) {
        throw new Error(`Booking ${bookingId} not found`);
      }

      if (bookingVerification.company_id !== companyId) {
        throw new Error(
          `Security error: Booking ${bookingId} (${
            bookingVerification.reference || "No reference"
          }) belongs to company ${
            bookingVerification.company_id
          }, not company ${companyId}`
        );
      }

      console.log(
        `Verified booking ${bookingId} (${
          bookingVerification.reference || "No reference"
        }) belongs to company ${companyId}`
      );

      // Additional safety check: ensure booking is not archived or cancelled
      if (
        bookingVerification.status === "archived" ||
        bookingVerification.status === "cancelled"
      ) {
        throw new Error(
          `Cannot import products to booking ${bookingId} (${
            bookingVerification.reference || "No reference"
          }) - status is ${bookingVerification.status}`
        );
      }

      const productsToInsert = parsedData.rows
        .filter((_, index) => !excludedRows.has(index))
        .map((row) => {
          const product: Record<string, unknown> = {
            booking_id: bookingId,
            company_id: companyId,
          };

          Object.entries(columnMapping).forEach(([csvColumn, dbColumn]) => {
            // Skip if dbColumn is undefined or empty
            if (!dbColumn) return;

            const csvColumnIndex = parsedData.headers.indexOf(csvColumn);
            let value = Array.isArray(row) ? row[csvColumnIndex] : undefined;

            // Convert values based on column type
            const dbColumnDef = DATABASE_COLUMNS.find(
              (col) => col.key === dbColumn
            );
            if (
              dbColumnDef &&
              value !== null &&
              value !== undefined &&
              value !== ""
            ) {
              if (
                dbColumnDef.type === "number" ||
                dbColumnDef.type === "integer" ||
                dbColumnDef.type === "bigint"
              ) {
                value = Number(value);
              }
            } else if (value === "" || value === null || value === undefined) {
              value = null;
            }

            product[dbColumn] = value;
          });

          return product;
        });

      // Insert products in batches
      const batchSize = 100;
      let processed = 0;

      for (let i = 0; i < productsToInsert.length; i += batchSize) {
        const batch = productsToInsert.slice(i, i + batchSize);

        const { error } = await supabase.from("booking_product").insert(batch);

        if (error) {
          throw error;
        }

        processed += batch.length;
        setImportProgress((processed / productsToInsert.length) * 100);
      }

      console.log(
        `Successfully imported ${
          productsToInsert.length
        } products for company ${companyId} to booking ${bookingId} (${
          bookingVerification.reference || "No reference"
        })`
      );
      message.success(
        `Successfully imported ${
          productsToInsert.length
        } products to booking ${bookingId} (${
          bookingVerification.reference || "EFF" + bookingId
        })!`
      );
      onSuccess();
      handleReset();
    } catch (error) {
      console.error("Error importing products:", error);
      message.error("Error importing products. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  // Reset component state
  const handleReset = () => {
    setCurrentStep(0);
    setFileList([]);
    setParsedData(null);
    setColumnMapping({});
    setValidationErrors([]);
    setImportPreview([]);
    setShowImportPreview(false);
    setExcludedRows(new Set());
    setImporting(false);
    setImportProgress(0);
    setEditedValues(new Map());
  };

  // Download template
  const downloadTemplate = () => {
    const templateData = [
      DATABASE_COLUMNS.map((col) => col.label),
      [
        "Sample Product 1", // name
        "1234567890", // hs_code
        "10.5", // gross_weight
        "0.025", // cbm
        "100.00", // value
        "1234567890123", // ean
        "10", // pieces
        "2", // cartons
      ],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    XLSX.writeFile(workbook, "booking_products_template.xlsx");
  };

  // Row exclusion handlers
  const handleRowToggle = (rowIndex: number) => {
    setExcludedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
    // Reset validation when rows change
    setValidationErrors([]);
    setShowImportPreview(false);
    setImportPreview([]);
  };

  const handleSelectAllRows = (checked: boolean) => {
    if (checked) {
      setExcludedRows(new Set());
    } else {
      const allRowIndices = parsedData?.preview.map((_, index) => index) || [];
      setExcludedRows(new Set(allRowIndices));
    }
    // Reset validation when rows change
    setValidationErrors([]);
    setShowImportPreview(false);
    setImportPreview([]);
  };

  // Preview table columns
  const previewColumns: ColumnsType<Record<string, unknown>> = parsedData
    ? [
        {
          title: (
            <div style={{ textAlign: "center" }}>
              <input
                type="checkbox"
                checked={excludedRows.size === 0}
                onChange={(e) => handleSelectAllRows(e.target.checked)}
                style={{ marginRight: 4 }}
              />
              Include
            </div>
          ),
          key: "include",
          width: 80,
          fixed: "left",
          render: (_: unknown, __: Record<string, unknown>, index: number) => (
            <div style={{ textAlign: "center" }}>
              <input
                type="checkbox"
                checked={!excludedRows.has(index)}
                onChange={() => handleRowToggle(index)}
              />
            </div>
          ),
        },
        {
          title: "Row",
          key: "rowNumber",
          width: 60,
          fixed: "left",
          render: (_: unknown, __: Record<string, unknown>, index: number) => (
            <Text type={excludedRows.has(index) ? "secondary" : undefined}>
              {index + 1}
            </Text>
          ),
        },
        ...parsedData.headers.map((header) => ({
          title: header,
          dataIndex: header,
          key: header,
          ellipsis: true,
          width: 150,
          render: (value: unknown, _, index: number) => (
            <Text type={excludedRows.has(index) ? "secondary" : undefined}>
              {value?.toString() || ""}
            </Text>
          ),
        })),
      ]
    : [];

  // Helper function to create a unique key for validation errors
  const getErrorKey = (error: ValidationError) =>
    `${error.row}-${error.column}`;

  // Handle value editing for validation errors
  const handleValueEdit = (error: ValidationError, newValue: string) => {
    const errorKey = getErrorKey(error);
    setEditedValues((prev) => {
      const newMap = new Map(prev);
      // Store empty string as explicit null indicator
      newMap.set(errorKey, newValue);
      return newMap;
    });
  };

  // Get the current value for an error (edited value or original)
  const getCurrentValue = (error: ValidationError): string => {
    const errorKey = getErrorKey(error);
    const editedValue = editedValues.get(errorKey);
    if (editedValue === null) return "";
    if (editedValue !== undefined) return editedValue;
    return String(error.value || "");
  };

  // Check if a value is explicitly set to null
  const isSetToNull = (error: ValidationError): boolean => {
    const errorKey = getErrorKey(error);
    return editedValues.has(errorKey) && editedValues.get(errorKey) === null;
  };

  // Validation errors table columns
  const errorColumns: ColumnsType<ValidationError> = [
    {
      title: "Row",
      dataIndex: "row",
      key: "row",
      width: 60,
      render: (row: number) => (row === -1 ? "Config" : row),
    },
    {
      title: "Column",
      dataIndex: "column",
      key: "column",
      width: 120,
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      width: 100,
      render: (value: unknown) =>
        value === null || value === undefined ? "(empty)" : String(value),
    },
    {
      title: "Error",
      dataIndex: "error",
      key: "error",
    },
    {
      title: "Edit Value",
      key: "editValue",
      width: 220,
      render: (_, error: ValidationError) => {
        const currentValue = getCurrentValue(error);
        const isNull = isSetToNull(error);

        return (
          <div style={{ width: "100%" }}>
            {isNull ? (
              <div
                style={{ display: "flex", gap: "4px", alignItems: "center" }}
              >
                <Tag color="orange">NULL</Tag>
                <Button
                  size="small"
                  onClick={() =>
                    handleValueEdit(error, String(error.value || ""))
                  }
                  title="Restore original value"
                >
                  Restore
                </Button>
                <Button
                  size="small"
                  onClick={() => handleValueEdit(error, "")}
                  title="Set to empty string"
                >
                  Empty
                </Button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "4px" }}>
                <Input
                  size="small"
                  value={currentValue}
                  onChange={(e) => handleValueEdit(error, e.target.value)}
                  placeholder="Enter corrected value"
                  style={{ flex: 1 }}
                />
                <Button
                  size="small"
                  onClick={() => handleValueEdit(error, "")}
                  title="Set to NULL"
                  type="default"
                >
                  NULL
                </Button>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      title="Import Booking Products"
      open={visible}
      onCancel={() => {
        handleReset();
        onCancel();
      }}
      width={1000}
      footer={null}
      destroyOnClose
    >
      <div style={{ marginBottom: 24 }}>
        <Steps current={currentStep} size="small">
          <Step title="Upload File" icon={<UploadOutlined />} />
          <Step title="Select Rows" icon={<FileTextOutlined />} />
          <Step title="Map Columns" icon={<FileTextOutlined />} />
          <Step title="Import" icon={<CheckCircleOutlined />} />
        </Steps>
      </div>

      {/* Step 0: File Upload */}
      {currentStep === 0 && (
        <Card>
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <div>
              <Title level={4}>Step 1: Upload File</Title>
              <Text type="secondary">
                Upload a CSV or Excel file containing product data. The system
                will automatically detect the product table even if there's
                header information at the top.
              </Text>
            </div>

            <Alert
              message="Supported File Formats"
              description="CSV (.csv), Excel (.xlsx, .xls) - Files with company headers and metadata are supported"
              type="info"
              showIcon
            />

            <Row gutter={16}>
              <Col span={12}>
                <Upload.Dragger
                  name="file"
                  fileList={fileList}
                  beforeUpload={handleFileUpload}
                  accept=".csv,.xlsx,.xls"
                  onChange={({ fileList }) => setFileList(fileList)}
                  onRemove={() => {
                    setFileList([]);
                    setParsedData(null);
                    return true;
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag file to this area to upload
                  </p>
                  <p className="ant-upload-hint">
                    Upload CSV or Excel file with product data
                  </p>
                </Upload.Dragger>
              </Col>

              <Col span={12}>
                <Card size="small" title="Need a template?">
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Text>
                      Download a template with the correct column structure:
                    </Text>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={downloadTemplate}
                      block
                    >
                      Download Template
                    </Button>
                  </Space>
                </Card>
              </Col>
            </Row>

            {parsedData && (
              <Card size="small" title="File Preview" style={{ marginTop: 16 }}>
                <Table
                  columns={previewColumns}
                  dataSource={parsedData.preview}
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                />
                <div
                  style={{
                    marginTop: 8,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text type="secondary">
                    Showing all rows. Total rows: {parsedData.rows.length}
                  </Text>
                  {excludedRows.size > 0 && (
                    <Text type="warning">
                      {excludedRows.size} row(s) excluded from import
                    </Text>
                  )}
                </div>
              </Card>
            )}
          </Space>
        </Card>
      )}

      {/* Step 1: Select Rows */}
      {currentStep === 1 && parsedData && (
        <Card>
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <div>
              <Title level={4}>Step 2: Select Rows</Title>
              <Text type="secondary">
                Review the imported data and select which rows to include in the
                import. Rows that appear to be totals or summaries are
                automatically excluded.
              </Text>
            </div>

            <Alert
              message="Row Selection"
              description="Use the checkboxes to include/exclude rows. You can select all or none using the header checkbox."
              type="info"
              showIcon
            />

            {/* Row Selection Controls */}
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Button size="small" onClick={() => handleSelectAllRows(true)}>
                  Select All
                </Button>
                <Button size="small" onClick={() => handleSelectAllRows(false)}>
                  Select None
                </Button>
                {excludedRows.size > 0 && (
                  <Text type="warning">
                    {excludedRows.size} row(s) excluded from import
                  </Text>
                )}
              </Space>
            </div>

            {/* Data Preview Table */}
            <Card title="Data Preview" size="small">
              <Table
                columns={previewColumns}
                dataSource={parsedData.preview}
                pagination={false}
                size="small"
                scroll={{ x: true }}
              />
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text type="secondary">
                  Showing all rows. Total rows: {parsedData.rows.length}
                </Text>
                {excludedRows.size > 0 && (
                  <Text type="warning">
                    {excludedRows.size} row(s) excluded from import
                  </Text>
                )}
              </div>
            </Card>

            <Divider />

            <Row justify="space-between">
              <Col>
                <Button onClick={() => setCurrentStep(0)}>Back</Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={() => setCurrentStep(2)}
                  disabled={parsedData.rows.length - excludedRows.size === 0}
                >
                  Continue to Column Mapping
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>
      )}

      {/* Step 2: Column Mapping */}
      {currentStep === 2 && parsedData && (
        <Card>
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <div>
              <Title level={4}>Step 3: Map Columns</Title>
              <Text type="secondary">
                Map your file columns to the database fields. Required fields
                are marked with *.
              </Text>
            </div>

            {/* Mapping Status Overview */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <Title level={5} style={{ margin: 0 }}>
                  📋 Mapping Status
                </Title>
                {(() => {
                  const totalRequired = DATABASE_COLUMNS.filter(
                    (col) => col.required
                  ).length;
                  const mappedRequired = DATABASE_COLUMNS.filter(
                    (col) =>
                      col.required &&
                      Object.values(columnMapping).includes(col.key)
                  ).length;
                  const totalMapped =
                    Object.values(columnMapping).filter(Boolean).length;

                  return (
                    <Space>
                      <Tag
                        color={
                          mappedRequired === totalRequired ? "green" : "orange"
                        }
                      >
                        Required: {mappedRequired}/{totalRequired}
                      </Tag>
                      <Tag color="blue">
                        Total: {totalMapped}/{DATABASE_COLUMNS.length}
                      </Tag>
                    </Space>
                  );
                })()}
              </div>
              <Row gutter={[8, 8]}>
                {DATABASE_COLUMNS.map((col) => {
                  const isMapped = Object.values(columnMapping).includes(
                    col.key
                  );
                  const mappedToHeader = Object.keys(columnMapping).find(
                    (header) => columnMapping[header] === col.key
                  );

                  return (
                    <Col span={8} key={col.key}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "4px",
                        }}
                      >
                        {isMapped ? (
                          <Tag color="green">✓ {col.label}</Tag>
                        ) : col.required ? (
                          <Tag color="red">✗ {col.label} *</Tag>
                        ) : (
                          <Tag color="default">○ {col.label}</Tag>
                        )}
                        {mappedToHeader && (
                          <Text type="secondary" style={{ fontSize: "11px" }}>
                            ← {mappedToHeader}
                          </Text>
                        )}
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Card>

            <Row gutter={[16, 16]}>
              {parsedData.headers.map((header) => {
                // Get list of already mapped database columns (excluding current header's mapping)
                const alreadyMappedColumns = Object.entries(columnMapping)
                  .filter(([h]) => h !== header)
                  .map(([, dbCol]) => dbCol);

                return (
                  <Col span={12} key={header}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Text strong>{header}</Text>
                      <Select
                        placeholder="Select database column"
                        style={{ width: "100%" }}
                        value={columnMapping[header]}
                        onChange={(value) =>
                          handleColumnMappingChange(header, value)
                        }
                        allowClear
                      >
                        {DATABASE_COLUMNS.map((col) => {
                          const isAlreadyMapped = alreadyMappedColumns.includes(
                            col.key
                          );

                          return (
                            <Option
                              key={col.key}
                              value={col.key}
                              disabled={isAlreadyMapped}
                            >
                              {col.label}{" "}
                              {col.required && <Tag color="red">Required</Tag>}
                              {isAlreadyMapped && (
                                <Tag color="red">Duplicated</Tag>
                              )}
                            </Option>
                          );
                        })}
                      </Select>
                    </Space>
                  </Col>
                );
              })}
            </Row>

            <Divider />

            <Row justify="space-between">
              <Col>
                <Button onClick={() => setCurrentStep(1)}>Back</Button>
              </Col>
              <Col>
                <Button type="primary" onClick={validateData}>
                  Validate & Continue
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>
      )}

      {/* Validation Errors or Import Preview */}
      {validationErrors.length > 0 && !showImportPreview && (
        <Card title="Validation Errors" style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <Alert
              message={`Found ${validationErrors.length} validation errors`}
              description="Edit the problematic values directly in the table below, then click 'Revalidate' to check for remaining errors."
              type="error"
              showIcon
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <Space>
              <Button
                size="small"
                onClick={() => setEditedValues(new Map())}
                disabled={editedValues.size === 0}
              >
                Clear All Edits
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  // Revalidate with edited values
                  const errors: ValidationError[] = [];

                  parsedData!.rows
                    .filter((_, index) => !excludedRows.has(index))
                    .forEach((row, rowIndex) => {
                      Object.entries(columnMapping).forEach(
                        ([header, dbColumn]) => {
                          // Skip if dbColumn is undefined or empty
                          if (!dbColumn) return;

                          const headerIndex =
                            parsedData!.headers.indexOf(header);
                          let value = Array.isArray(row)
                            ? row[headerIndex] || null
                            : null;

                          // Check if this value has been edited
                          const errorKey = `${rowIndex}-${dbColumn}`;
                          if (editedValues.has(errorKey)) {
                            const editedValue = editedValues.get(errorKey);
                            value =
                              editedValue === "" ? null : editedValue || null;
                          }

                          // Validate the value
                          const dbCol = DATABASE_COLUMNS.find(
                            (col) => col.key === dbColumn
                          );
                          if (
                            dbCol &&
                            value !== null &&
                            value !== undefined &&
                            value !== ""
                          ) {
                            if (dbCol.type === "number") {
                              const numValue = parseFloat(String(value));
                              if (isNaN(numValue)) {
                                errors.push({
                                  row: rowIndex,
                                  column: dbColumn,
                                  value,
                                  error: "Must be a valid number",
                                });
                              }
                            } else if (dbCol.type === "integer") {
                              const intValue = parseInt(String(value), 10);
                              if (isNaN(intValue)) {
                                errors.push({
                                  row: rowIndex,
                                  column: dbColumn,
                                  value,
                                  error: "Must be a valid integer",
                                });
                              }
                            } else if (dbCol.type === "bigint") {
                              const bigintValue = parseInt(String(value), 10);
                              if (isNaN(bigintValue)) {
                                errors.push({
                                  row: rowIndex,
                                  column: dbColumn,
                                  value,
                                  error: "Must be a valid number",
                                });
                              }
                            }
                          }
                        }
                      );
                    });

                  setValidationErrors(errors);

                  if (errors.length === 0) {
                    // Create import preview with edited values
                    const preview = parsedData!.rows
                      .filter((_, index) => !excludedRows.has(index))
                      .map((row, index) => {
                        const previewRow: Record<string, unknown> = {
                          row_number: index + 1,
                        };

                        Object.entries(columnMapping).forEach(
                          ([header, dbColumn]) => {
                            if (dbColumn) {
                              const headerIndex =
                                parsedData!.headers.indexOf(header);
                              let value = Array.isArray(row)
                                ? row[headerIndex] || null
                                : null;

                              // Use edited value if available
                              const errorKey = `${index}-${dbColumn}`;
                              if (editedValues.has(errorKey)) {
                                const editedValue = editedValues.get(errorKey);
                                value =
                                  editedValue === ""
                                    ? null
                                    : editedValue || null;
                              }

                              // Convert value based on database column type
                              const dbCol = DATABASE_COLUMNS.find(
                                (col) => col.key === dbColumn
                              );
                              if (
                                dbCol &&
                                value !== null &&
                                value !== undefined &&
                                value !== ""
                              ) {
                                if (dbCol.type === "number") {
                                  const numValue = parseFloat(String(value));
                                  value = isNaN(numValue) ? null : numValue;
                                } else if (dbCol.type === "integer") {
                                  const intValue = parseInt(String(value), 10);
                                  value = isNaN(intValue) ? null : intValue;
                                } else if (dbCol.type === "bigint") {
                                  const bigintValue = parseInt(
                                    String(value),
                                    10
                                  );
                                  value = isNaN(bigintValue)
                                    ? null
                                    : bigintValue;
                                }
                              }

                              previewRow[dbColumn] = value;
                            }
                          }
                        );

                        return previewRow;
                      });

                    setImportPreview(preview);
                    setShowImportPreview(true);
                    setCurrentStep(3);
                    message.success(
                      "All validation errors fixed! Proceeding to import."
                    );
                  } else {
                    message.error(
                      `Still ${errors.length} validation errors remaining. Please fix them.`
                    );
                  }
                }}
              >
                Revalidate
              </Button>
            </Space>
          </div>

          <Table
            columns={errorColumns}
            dataSource={validationErrors}
            pagination={{ pageSize: 10 }}
            size="small"
            rowKey={(record, index) =>
              `${record.row}-${record.column}-${index}`
            }
          />
        </Card>
      )}

      {/* Import Preview - shows when validation passes */}
      {showImportPreview && importPreview.length > 0 && (
        <Card title="✅ Import Preview" style={{ marginTop: 16 }}>
          <Alert
            message={`Ready to import ${importPreview.length} products`}
            description="Review the mapped data below. This is exactly what will be imported to your booking."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Table
            columns={[
              {
                title: "Row",
                dataIndex: "row_number",
                key: "row_number",
                width: 60,
                fixed: "left",
              },
              ...DATABASE_COLUMNS.filter((col) =>
                Object.values(columnMapping).includes(col.key)
              ).map((col) => ({
                title: col.label,
                dataIndex: col.key,
                key: col.key,
                width: 150,
                ellipsis: true,
                render: (value: unknown) => {
                  if (value === null || value === undefined) {
                    return <Text type="secondary">(empty)</Text>;
                  }
                  if (col.type === "number" && typeof value === "number") {
                    return value.toFixed(2);
                  }
                  return String(value);
                },
              })),
            ]}
            dataSource={importPreview}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            size="small"
            scroll={{ x: true }}
            rowKey="key"
          />
        </Card>
      )}

      {/* Step 3: Import */}
      {currentStep === 3 && (
        <Card>
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <div>
              <Title level={4}>Step 4: Import Products</Title>
              <Text type="secondary">
                Confirm the details below before importing products.
              </Text>
            </div>

            <Alert
              message="⚠️ Import Confirmation"
              description={
                <div>
                  <p>
                    <strong>
                      You are about to import {parsedData?.rows.length} products
                      to:
                    </strong>
                  </p>
                  <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                    <li>
                      <strong>Booking ID:</strong> {bookingId}
                    </li>
                    <li>
                      <strong>Company ID:</strong> {companyId}
                    </li>
                    <li>
                      <strong>Mapped columns:</strong>{" "}
                      {Object.keys(columnMapping).length} of{" "}
                      {DATABASE_COLUMNS.length} available
                    </li>
                  </ul>
                  <p
                    style={{
                      margin: "8px 0 0 0",
                      fontSize: "12px",
                      color: "#666",
                    }}
                  >
                    <strong>Important:</strong> Once imported, these products
                    will be permanently associated with this booking. Please
                    verify this is the correct booking before proceeding.
                  </p>
                </div>
              }
              type="warning"
              showIcon
            />

            {importing && (
              <div>
                <Text>Importing products...</Text>
                <Progress percent={Math.round(importProgress)} />
              </div>
            )}

            <Row justify="space-between">
              <Col>
                <Button
                  onClick={() => {
                    setCurrentStep(2);
                    setShowImportPreview(false);
                    setImportPreview([]);
                  }}
                  disabled={importing}
                >
                  Back to Column Mapping
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={handleImport}
                  loading={importing}
                  disabled={importing}
                >
                  Import Products
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>
      )}
    </Modal>
  );
};
